import Vue from 'vue';
import router from '@/router';
import axios from 'axios';
import VueAxios from 'vue-axios';

Vue.use(VueAxios, axios);

const API_URL = process.env.VUE_APP_API_BASE_URL || '';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// Bootstrap initial token into Axios immediately when script loads
const initialToken = localStorage.getItem(TOKEN_KEY);
if (initialToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${initialToken}`;
}

export default {
    namespaced: true,

    state: () => ({
        isAuthenticated: Boolean(initialToken),
        token: initialToken || null,
        refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || null,
        tokenExpiry: localStorage.getItem(TOKEN_EXPIRY_KEY) || null,
        user: null,
        loading: false,
        error: null
    }),

    getters: {
        isAuthenticated: (state) => state.isAuthenticated && state.token !== null,
        token: (state) => state.token,
        refreshToken: (state) => state.refreshToken,
        user: (state) => state.user,
        isLoading: (state) => state.loading,
        error: (state) => state.error,
        tokenExpiry: (state) => state.tokenExpiry,
        isTokenValid: (state) => {
            if (!state.tokenExpiry) return false;
            return new Date(state.tokenExpiry) > new Date();
        }
    },

    mutations: {
        SET_TOKEN(state, token) {
            state.token = token;
            if (token) {
                localStorage.setItem(TOKEN_KEY, token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            } else {
                localStorage.removeItem(TOKEN_KEY);
                delete axios.defaults.headers.common['Authorization'];
            }
        },

        SET_REFRESH_TOKEN(state, refreshToken) {
            state.refreshToken = refreshToken;
            if (refreshToken) {
                localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
            } else {
                localStorage.removeItem(REFRESH_TOKEN_KEY);
            }
        },

        SET_TOKEN_EXPIRY(state, expiry) {
            state.tokenExpiry = expiry;
            if (expiry) {
                localStorage.setItem(TOKEN_EXPIRY_KEY, expiry);
            } else {
                localStorage.removeItem(TOKEN_EXPIRY_KEY);
            }
        },

        SET_AUTHENTICATED(state, isAuthenticated) {
            state.isAuthenticated = isAuthenticated;
        },

        SET_USER(state, user) {
            state.user = user;
        },

        SET_LOADING(state, loading) {
            state.loading = loading;
        },

        SET_ERROR(state, error) {
            state.error = error;
        },

        CLEAR_AUTH(state) {
            state.isAuthenticated = false;
            state.token = null;
            state.refreshToken = null;
            state.tokenExpiry = null;
            state.user = null;
            state.error = null;

            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
            delete axios.defaults.headers.common['Authorization'];
        }
    },

    actions: {
        /**
         * Initialize auth state from localStorage on App Boot
         */
        async initializeAuth({ commit, dispatch, state }) {
            const token = localStorage.getItem(TOKEN_KEY);
            if (!token) return;

            commit('SET_TOKEN', token);
            commit('SET_AUTHENTICATED', true);

            const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
            if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
                return await dispatch('refreshToken');
            }

            return await dispatch('fetchUser');
        },

        /**
         * Login user
         */
        async login({ commit, dispatch }, payload) {
            try {
                commit('SET_LOADING', true);
                commit('SET_ERROR', null);

                const response = await axios.post(`${API_URL}/login`, payload.user, payload.requestOptions);
                const { data, meta } = response.data;

                if (data.access_token) {
                    commit('SET_TOKEN', data.access_token);
                    commit('SET_REFRESH_TOKEN', data.refresh_token);

                    const expiresIn = meta?meta.expires_in : 86400;
                    const expiryTime = new Date(Date.now() + expiresIn * 1000);
                    commit('SET_TOKEN_EXPIRY', expiryTime.toISOString());
                }

                await dispatch('fetchUser');
                commit('SET_AUTHENTICATED', true);

                router.push({ name: 'Home' });
                return response.data;
            } catch (error) {
                const errorMessage = error.response?error.response.data.message : 'Login failed.';
                commit('SET_ERROR', errorMessage);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },

        /**
         * Fetch current user
         */
        async fetchUser({ commit, dispatch, state }) {
            try {
                if (!state.token) return null;

                const response = await axios.get(`${API_URL}/me`);
                const userData = response.data.data || response.data;

                commit('SET_USER', userData);
                return userData;

            } catch (error) {
                console.error('Failed to fetch user:', error);
                if (error.response && error.response.status === 401) {
                    // Fixed 'this.dispatch' bug -> uses action context 'dispatch'
                    await dispatch('refreshToken');
                }
                return null;
            }
        },

        /**
         * Refresh token
         */
        async refreshToken({ commit, state }) {
            try {
                if (!state.refreshToken) {
                    throw new Error('No refresh token available');
                }

                const response = await axios.post(`${API_URL}/token/refresh`, {
                    refresh_token: state.refreshToken
                });

                const { data } = response.data;

                if (data.access_token) {
                    commit('SET_TOKEN', data.access_token);
                    if (data.refresh_token) {
                        commit('SET_REFRESH_TOKEN', data.refresh_token);
                    }

                    const expiresIn = data.expires_in || 86400;
                    const expiryTime = new Date(Date.now() + expiresIn * 1000);
                    commit('SET_TOKEN_EXPIRY', expiryTime.toISOString());

                    return true;
                }

                return false;

            } catch (error) {
                console.error('Token refresh failed:', error);
                commit('CLEAR_AUTH');
                router.push({ name: 'Login' });
                return false;
            }
        },

        /**
         * Logout user
         */
        async logout({ commit, dispatch }) {
            try {
                await axios.post(`${API_URL}/logout`);
            } catch (e) {
                console.warn('Logout API error:', e);
            } finally {
                commit('CLEAR_AUTH');
                router.push({ name: 'Login' });
            }
        }
    }
};